import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { SendOutlined } from "@mui/icons-material";
import {
  useChannelStateContext,
  useChatContext,
  useMessageContext,
} from "stream-chat-react";
import type { DirectPaymentAttachment } from "Components/Chat/chatAttachmentTypes";
import { identityUserPublicIdFromStreamId } from "Components/Chat/chatConstants";
import {
  defaultVariant,
  statusVariants,
} from "Components/Chat/transactionStatusUtils";
import { useTransactionPolling } from "Hooks/Chat/useTransactionPolling";
import { useNavigate } from "Navigation/useNavigate";
import { useResource } from "Translations/Resources";
import { formatCurrency } from "Utils/CurrencyUtils";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  attachment: DirectPaymentAttachment;
};

/**
 * One payment under a multi-payer payment request, rendered as a single line inside its (otherwise
 * standard) thread reply: payer, amount, and the live status where the viewer may read it.
 *
 * The transaction read admits only the creditor (the request author), the debtor (the payer) and
 * the creator (ADR-0009). Anyone else would get a 404 and a status frozen at "Created", so for them
 * the row neither polls nor shows a status, and does not link to the transaction detail either —
 * that page would 404 too. Both checks are client-side: the payer is the message sender, the
 * author is named on the parent request's attachment.
 */
export const DirectPaymentCompactRow = ({
  attachment: { transactionPublicID, amount, currencyCode, transactionStatus },
}: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { client } = useChatContext();
  const { thread } = useChannelStateContext();
  const { message } = useMessageContext();

  const viewerStreamId = client.userID;
  const isViewerPayer = !!viewerStreamId && message.user?.id === viewerStreamId;
  const isViewerAuthor =
    !!viewerStreamId &&
    (thread?.attachments ?? []).some(
      (a) =>
        a.type === "payment_request_multi" &&
        a.recipientIdentityUserPublicID ===
          identityUserPublicIdFromStreamId(viewerStreamId),
    );
  const isTransactionReadable = isViewerPayer || isViewerAuthor;

  const { data } = useTransactionPolling(
    isTransactionReadable ? transactionPublicID : null,
  );
  const status = data?.transactionStatus ?? transactionStatus;
  const variant = statusVariants[status] ?? defaultVariant;

  const content = (
    <Stack
      className="direct-payment-compact-row"
      direction="row"
      alignItems="center"
      gap={1}
      px={1.5}
      py={1}
      width="100%"
      minWidth={0}
      maxWidth={350}
      borderRadius={2}
      sx={{
        backgroundColor: isTransactionReadable
          ? variant.cardBackground
          : grey[100],
      }}
    >
      {/* The leading slot is the status for those who may read the transaction; everyone else
          sees the plain "sent" glyph. Icon only, the label survives as tooltip and accessible
          name. */}
      {isTransactionReadable ? (
        <Box
          role="img"
          aria-label={variant.labelKey(t)}
          title={variant.labelKey(t)}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={24}
          height={24}
          mr={0.5}
          borderRadius="50%"
          flexShrink={0}
          sx={{
            backgroundColor: variant.statusBackground,
            // The variant icons are sized for the full card's status bar; scale them to the badge.
            "& .MuiSvgIcon-root": { fontSize: 16 },
          }}
        >
          {variant.icon}
        </Box>
      ) : (
        <SendOutlined fontSize="small" color="action" sx={{ mr: 0.5 }} />
      )}
      {/* The row is as wide as the bubble lets it be (capped at 350px), and the name is the one
          flexible part: minWidth 0 lets it shrink below its content so noWrap can ellipsize, so
          a long name truncates exactly as much as the screen requires and no more. */}
      <Typography
        variant="subtitle2"
        fontWeight={700}
        noWrap
        flex={1}
        minWidth={0}
      >
        {message.user?.name}
      </Typography>
      <Typography variant="body2" fontWeight={700} flexShrink={0}>
        {formatCurrency({ amount, currencyCode })}
      </Typography>
    </Stack>
  );

  if (!isTransactionReadable) {
    return content;
  }

  return (
    <ButtonBase
      onClick={() =>
        navigate(`${appUrl("transaction-detail")}/${transactionPublicID}`)
      }
      sx={{ display: "block", textAlign: "left", borderRadius: 2 }}
    >
      {content}
    </ButtonBase>
  );
};
